import { useMutation } from '@tanstack/react-query';

import type { SwaggerResponse } from '@/services/config';
import {
    useBioscopeController_ActiveBioscopes,
    useBioscopeController_ActiveBioscopesForClient,
    useBioscopeController_CreateBioscope,
    useBioscopeController_DeleteBioscope,
    useBioscopeDetailController_BioscopeDetailNew,
    useMoveBioscopeController_DeleteLastMove,
} from '@/services/hooks';
import {
    BioscopeController_UpdateBioscope,
    MoveBioscopeController_MoveBioscope,
} from '@/services/services';
import type { ActiveBioscope, BioscopeDetail, UpdateBioscope } from '@/services/types';

import type { ApiError } from '../shared/errors';
import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';
import { sendWireBody } from '../shared/wireRequest';

import { toCreateBioscopeBody } from './mappers';
import type { BioscopeSettings, MoveBioscopeQueryParams, NewBioscopeInput } from './types';

function body<T>(value: T): T {
    return value;
}

/** Every bioscope in service, for the internal bioscope list. */
export function useGetActiveBioscopes(): DomainQuery<ActiveBioscope[]> {
    return useMappedQuery(useBioscopeController_ActiveBioscopes(), body<ActiveBioscope[]>);
}

/** The client's bioscopes in service. */
export function useGetClientBioscopes(clientId: number): DomainQuery<ActiveBioscope[]> {
    return useMappedQuery(
        useBioscopeController_ActiveBioscopesForClient(clientId),
        body<ActiveBioscope[]>
    );
}

export interface BioscopeDetailOptions {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
}

export function useGetBioscopeDetail(
    bioscopeId: number,
    options?: BioscopeDetailOptions
): DomainQuery<BioscopeDetail> {
    return useMappedQuery(
        useBioscopeDetailController_BioscopeDetailNew(bioscopeId, options),
        body<BioscopeDetail>
    );
}

function newBioscopeToApi(input: NewBioscopeInput) {
    return { requestBody: toCreateBioscopeBody(input) };
}

function settingsToApi({
    bioscopeId,
    ...settings
}: BioscopeSettings & { bioscopeId: number }): UpdateBioscopeVariables {
    return { bioscopeId, requestBody: settings };
}

interface BioscopeMove {
    bioscopeId: number;
    move: MoveBioscopeQueryParams;
}

function moveBioscope({ bioscopeId, move }: BioscopeMove): Promise<SwaggerResponse<unknown>> {
    return sendWireBody('postRequest', MoveBioscopeController_MoveBioscope, { bioscopeId }, move);
}

function sameMove(move: BioscopeMove): BioscopeMove {
    return move;
}

function bioscopeIdToApi(bioscopeId: number) {
    return { bioscopeId };
}

const ignore = (callbacks?: DomainMutationCallbacks) => toMutationOptions(callbacks, ignoreBody);

export function useCreateBioscope(
    callbacks?: DomainMutationCallbacks
): DomainMutation<NewBioscopeInput, void> {
    return useMappedMutation(
        useBioscopeController_CreateBioscope(ignore(callbacks)),
        newBioscopeToApi,
        ignoreBody
    );
}

/**
 * `UpdateBioscope` as the API declares it: `description` is nullable and null clears it. The
 * generated type turns nullable into optional, so it cannot carry the null.
 */
type UpdateBioscopeBody = Omit<UpdateBioscope, 'description'> & { description?: string | null };

interface UpdateBioscopeVariables {
    bioscopeId: number;
    requestBody: UpdateBioscopeBody;
}

function updateBioscope({
    bioscopeId,
    requestBody,
}: UpdateBioscopeVariables): Promise<SwaggerResponse<unknown>> {
    return sendWireBody(
        'patchRequest',
        BioscopeController_UpdateBioscope,
        { bioscopeId },
        requestBody
    );
}

/** Marks a bioscope as a test unit and sets its description, or clears it with null. */
export function useUpdateBioscopeSettings(
    callbacks?: DomainMutationCallbacks
): DomainMutation<BioscopeSettings & { bioscopeId: number }, void> {
    return useMappedMutation(
        useMutation<SwaggerResponse<unknown>, ApiError, UpdateBioscopeVariables>({
            mutationFn: updateBioscope,
            ...ignore(callbacks),
        }),
        settingsToApi,
        ignoreBody
    );
}

export function useDeleteBioscope(
    callbacks?: DomainMutationCallbacks
): DomainMutation<number, void> {
    return useMappedMutation(
        useBioscopeController_DeleteBioscope(ignore(callbacks)),
        bioscopeIdToApi,
        ignoreBody
    );
}

/** Places a bioscope in a pen (or on land) from a given time. */
export function useMoveBioscope(
    callbacks?: DomainMutationCallbacks
): DomainMutation<BioscopeMove, void> {
    return useMappedMutation(
        useMutation<SwaggerResponse<unknown>, ApiError, BioscopeMove>({
            mutationFn: moveBioscope,
            ...ignore(callbacks),
        }),
        sameMove,
        ignoreBody
    );
}

/** Undoes the bioscope's latest move. */
export function useDeleteLastBioscopeMove(
    callbacks?: DomainMutationCallbacks
): DomainMutation<number, void> {
    return useMappedMutation(
        useMoveBioscopeController_DeleteLastMove(ignore(callbacks)),
        bioscopeIdToApi,
        ignoreBody
    );
}
